import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';

// Import step components
import BasicInfoStep from './onboarding/BasicInfoStep';
import TravelPreferencesStep from './onboarding/TravelPreferencesStep';
import RelationshipGoalsStep from './onboarding/RelationshipGoalsStep';
import PhotosStep from './onboarding/PhotosStep';
import InterestsStep from './onboarding/InterestsStep';

const OnboardingPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  
  // Form data state
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    age: '',
    gender: '',
    city: '',
    state: '',
    country: '',
    occupation: '',
    education: '',
    
    // Step 2: Travel Preferences
    preferred_destinations: [],
    travel_styles: [],
    budget_min: 500,
    budget_max: 5000,
    travel_frequency: '',
    willing_to_travel_solo: true,
    preferred_group_size: '',
    
    // Step 3: Relationship Goals
    relationship_goals: [],
    cultural_background: '',
    religion: '',
    height: '',
    alcohol: null,
    smoking: null,
    
    // Step 4: Photos
    primary_photo: null,
    additional_photos: [],
    
    // Step 5: Interests
    interests: [],
    languages: [],
    bio: '',
  });
  
  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;
  
  const steps = [
    { number: 1, title: 'Basic Info', component: BasicInfoStep },
    { number: 2, title: 'Travel Style', component: TravelPreferencesStep },
    { number: 3, title: 'Looking For', component: RelationshipGoalsStep },
    { number: 4, title: 'Photos', component: PhotosStep },
    { number: 5, title: 'About You', component: InterestsStep },
  ];
  
  const currentStepData = steps[currentStep - 1];
  const StepComponent = currentStepData.component;
  
  const handleNext = async () => {
    setError('');
    setLoading(true);
    
    try {
      // Save current step data to backend
      switch (currentStep) {
        case 1:
          await api.post('/profile/basic-info', {
            age: parseInt(formData.age),
            gender: formData.gender,
            city: formData.city,
            state: formData.state,
            country: formData.country,
            occupation: formData.occupation,
            education: formData.education,
          });
          break;
        case 2:
          await api.post('/profile/travel-preferences', {
            preferred_destinations: formData.preferred_destinations,
            travel_styles: formData.travel_styles,
            budget_min: formData.budget_min,
            budget_max: formData.budget_max,
            travel_frequency: formData.travel_frequency,
            willing_to_travel_solo: formData.willing_to_travel_solo,
            preferred_group_size: formData.preferred_group_size,
          });
          break;
        case 3:
          await api.post('/profile/relationship-goals', {
            relationship_goals: formData.relationship_goals,
            cultural_background: formData.cultural_background,
            religion: formData.religion,
            height: formData.height ? parseInt(formData.height) : null,
            alcohol: formData.alcohol,
            smoking: formData.smoking,
          });
          break;
        case 4:
          await api.post('/profile/photos', {
            primary: formData.primary_photo,
            additional: formData.additional_photos,
          });
          break;
        case 5:
          const response = await api.post('/profile/interests', {
            interests: formData.interests,
            languages: formData.languages,
            bio: formData.bio,
          });
          
          // Update user in context
          const updatedUser = { ...user, profile_complete: true };
          updateUser(updatedUser);
          
          // Redirect to home
          navigate('/');
          return;
      }
      
      // Move to next step
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleSkip = () => {
    navigate('/');
  };
  
  const updateFormData = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img src="/logo-192.png" alt="Dostination" className="w-10 h-10 object-contain" />
              <span className="font-display text-2xl font-bold text-primary-800">
                Dostination
              </span>
            </div>
            <Button variant="ghost" onClick={handleSkip}>
              Skip for now
            </Button>
          </div>
        </div>
      </header>
      
      {/* Progress Bar */}
      <div className="bg-white border-b border-neutral-200">
        <div className="container mx-auto px-4 py-6">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center">
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all
                      ${currentStep > step.number
                        ? 'bg-success-500 text-white'
                        : currentStep === step.number
                        ? 'bg-primary-700 text-white'
                        : 'bg-neutral-200 text-neutral-500'
                      }
                    `}
                  >
                    {currentStep > step.number ? (
                      <CheckCircle2 className="h-6 w-6" />
                    ) : (
                      step.number
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`
                        w-12 md:w-24 h-1 mx-2 rounded transition-all
                        ${currentStep > step.number ? 'bg-success-500' : 'bg-neutral-200'}
                      `}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-sm">
              {steps.map((step) => (
                <span
                  key={step.number}
                  className={`
                    hidden md:block font-medium transition-all
                    ${currentStep === step.number
                      ? 'text-primary-700'
                      : currentStep > step.number
                      ? 'text-success-600'
                      : 'text-neutral-500'
                    }
                  `}
                >
                  {step.title}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-2xl mx-auto">
          {/* Step Title */}
          <div className="text-center mb-8">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-2">
              {currentStepData.title}
            </h1>
            <p className="text-neutral-600">
              Step {currentStep} of {totalSteps}
            </p>
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}
          
          {/* Step Component */}
          <div className="bg-white rounded-2xl shadow-soft p-6 md:p-8 mb-6">
            <StepComponent
              formData={formData}
              updateFormData={updateFormData}
            />
          </div>
          
          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1 || loading}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            <Button
              onClick={handleNext}
              disabled={loading}
              size="lg"
              className="min-w-[140px]"
            >
              {loading ? (
                'Saving...'
              ) : currentStep === totalSteps ? (
                'Complete'
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OnboardingPage;
